/**
 * Purchase attribution
 * ---------------------------------------------------------------------------
 * Ties a CTA click on a marketing surface (e.g. the /vision page) to the
 * purchase flow that follows it, so end-to-end conversion — preselection,
 * submit, success/failure and receipt view — can be measured.
 *
 * Privacy: the record lives in sessionStorage only (per tab, cleared when the
 * tab closes), contains no PII, and expires after 30 minutes.
 */
export type AttributionToken = "MEAL2" | "MEAL4";

export type PurchaseAttribution = {
  /** Surface that produced the click, e.g. "vision_cta". */
  source: string;
  /** Token that was preselected by the CTA. */
  token: AttributionToken;
  /** Epoch ms of the click. */
  ts: number;
};

const KEY = "sfn.attr";
/** Attribution older than this is considered a new, unattributed session. */
export const ATTRIBUTION_TTL_MS = 30 * 60 * 1000;

export function setPurchaseAttribution(source: string, token: AttributionToken): void {
  if (typeof window === "undefined") return;
  try {
    const record: PurchaseAttribution = { source, token, ts: Date.now() };
    window.sessionStorage.setItem(KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable — attribution is best-effort */
  }
}

export function getPurchaseAttribution(): PurchaseAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PurchaseAttribution>;
    if (!parsed || typeof parsed.source !== "string") return null;
    if (parsed.token !== "MEAL2" && parsed.token !== "MEAL4") return null;
    if (typeof parsed.ts !== "number" || Date.now() - parsed.ts > ATTRIBUTION_TTL_MS) {
      clearPurchaseAttribution();
      return null;
    }
    return { source: parsed.source, token: parsed.token, ts: parsed.ts };
  } catch {
    return null;
  }
}

export function clearPurchaseAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Analytics props describing the attribution of the current purchase step.
 * Returns an empty object when the flow is organic (no stored CTA click).
 */
export function attributionProps(token?: AttributionToken): Record<string, unknown> {
  const a = getPurchaseAttribution();
  if (!a) return { attr_source: "organic" };
  return {
    attr_source: a.source,
    attr_token: a.token,
    attr_age_ms: Date.now() - a.ts,
    ...(token ? { attr_match: a.token === token } : {}),
  };
}
